import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import DOMPurify from 'dompurify';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { fmt } from '../../lib/formatters';
import { useVolver } from '../../lib/useVolver';
import { useTituloRegistro } from '../../lib/page-title';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { confirmar, toast } from '../../lib/toast';
import { DataTable, type Column } from '../../components/DataTable';
import { ProductoArchivos, ProductoFotos } from '../../components/ProductoArchivos';
import { RotacionSubtipo } from '../../components/producto/RotacionSubtipo';
import { DescripcionProveedor, useHeredadoProveedor } from '../../components/producto/HeredadoProveedor';
import { AccionComercialBadge } from '../../components/producto/AccionComercial';
import { ProductoScrapeModal } from '../../components/ProductoScrapeModal';
import { CosteEntradaFicha } from '../../components/CosteEntradaFicha';
import { VentasApproxFicha } from '../../components/VentasApproxFicha';
import { ProductoEditarModal, type ProductoEditable } from '../../components/producto/ProductoEditarModal';
import { EntregasProductoSection, HistoricoPrecioVentaSection } from '../../components/producto/PedidosEntregas';
import { ProductoProveedorDetalleView } from '../catalogo/ProductoProveedorDetalle';
import { GameCalculadora } from '../game/GameCalculadora';

/**
 * Ficha de producto del rediseño UX (docs/rediseno-ux/propuesta.html, fase 3):
 *   - cabecera con nombre, identificadores, estado y los CINCO hechos (D2 del
 *     CEO): coste de entrada, precio al cliente, margen, stock con cobertura,
 *     próxima entrada;
 *   - smart buttons con contador que saltan a su sección;
 *   - barra de anclas fija; secciones en una sola página, cada dato una vez;
 *   - panel lateral con alertas, proveedor, próximo hito y sustitución.
 * Los datos vienen de /productos/:id/full (maestro, cotizaciones, series) y
 * /productos/:id/resumen (cabecera y contadores). Las secciones pesadas
 * (coste de entrada, ventas APPROX, pedidos, archivos) cargan las suyas.
 */

type Cotizacion = {
  id: string; fecha: string; unidades: number | null; coste_usd: number | string | null; cambio_usd_eur: number | string | null;
  cdpe_eur: number | string | null; coste_real_eur: number | string | null; coste_game_final_eur: number | string | null; pvpr_iva_eur: number | string | null; notas: string | null;
};
type SemanaRow = { fecha_informe: string; semana_inicio: string; semana_fin: string; unidades_vendidas: number | null; stock_actual: number | null };
type Full = ProductoEditable & {
  cliente_id: string; cliente_nombre: string | null; categoria_id: string | null; gama_nombre: string | null; detalle_nombre: string | null;
  proveedor_nombre: string | null; producto_proveedor_id: string | null; estado_nombre: string | null; estado_color: string | null; fecha_creacion: string;
  sustituido_por_id: string | null; sustituido_por_nombre: string | null; sustituye_a: { id: string; sku: string; nombre: string }[];
  cotizaciones: Cotizacion[]; historico_semanal: SemanaRow[];
  metricas: { stock_actual: number | null; stock_tiendas: number | null; stock_fecha: string | null; ventas_l4wk: number; ventas_l12wk: number; media_semanal_l4: number; semanas_stock: number | null; uds_pedidas: number };
};
type Resumen = {
  cabecera: {
    coste_entrada: { valor: number | null; fecha: string; uds: number; albaran: string | null } | null;
    coste_teorico: number | null; pvpr: number | null;
    precio_cliente: { valor: number; fecha: string; fuente: 'ventas_approx' | 'pedido_cliente'; ref: string | null; uds: number | null; pedido_id: string | null } | null;
    margen_pct: number | null;
    stock: { actual: number | null; fecha: string | null; cobertura: number | null; unidad: 'meses' | 'semanas' | null; venta_media: number | null; provisional: boolean; umbral: number | null };
    proxima_entrada: { uds: number; eta: string | null; etd: string | null; pedido_id: string | null; numero: string | null; estado: string | null; fuente: 'pedido_cliente' | 'extracto_cliente'; total_pendiente: number } | null;
  };
  contadores: { pedidos: number; pedidos_abiertos: number; cotizaciones: number; entregas: number; ventas_approx: number; abonos_approx: number; archivos: number; pedidos_proveedor: number; abiertos_pendientes: number };
  alertas: { tono: 'good' | 'warn' | 'bad' | 'neutral'; titulo: string; detalle: string }[];
  ventas_mensuales: { mes: string; unidades: number; provisional: boolean }[];
  ventas_semanales: { semana_inicio: string; unidades: number; stock: number | null }[];
  ventas_approx_hasta: string | null;
};

const SECCIONES = [
  { key: 'general', label: 'General' },
  { key: 'precios', label: 'Precios y costes' },
  { key: 'stock', label: 'Stock y ventas' },
  { key: 'coste-entrada', label: 'Coste de entrada' },
  { key: 'ventas-approx', label: 'Ventas APPROX' },
  { key: 'pedidos', label: 'Pedidos y entregas' },
  { key: 'proveedor', label: 'Proveedor' },
  { key: 'documentos', label: 'Documentos' },
] as const;
type SeccionKey = (typeof SECCIONES)[number]['key'];

export function ProductoFicha() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const full = useQuery({ queryKey: ['producto', id, 'full'], queryFn: () => api.get<Full>(`/productos/${id}/full`), enabled: !!id });
  const resumen = useQuery({ queryKey: ['producto', id, 'resumen'], queryFn: () => api.get<Resumen>(`/productos/${id}/resumen`), enabled: !!id });
  const [editando, setEditando] = useState(false);
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [calculadora, setCalculadora] = useState(false);
  const [verMas, setVerMas] = useState(false);
  // Sin ficha comercial propia se enseña la del producto de proveedor, marcada.
  const heredado = useHeredadoProveedor(id ?? '', !!id && full.isSuccess && !full.data?.ficha_descripcion?.trim());
  const [panel, setPanel] = useState(() => { try { return localStorage.getItem('pm-approx:ficha-panel') !== 'off'; } catch { return true; } });
  // Pestañas (decisión del CEO, 16/09/2026): cada sección es una pestaña y
  // solo se pinta la activa; va en la URL (?tab=) para poder enlazarla.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabUrl = searchParams.get('tab');
  const tab: SeccionKey = SECCIONES.some((s) => s.key === tabUrl) ? (tabUrl as SeccionKey) : 'general';

  const volver = useVolver(() => (full.data?.cliente_codigo ? `/${full.data.cliente_codigo.toLowerCase()}/productos` : '/'));
  useTituloRegistro(full.data ? `${full.data.sku} · ${full.data.nombre}` : null);

  // Enlace perezoso de la ficha de proveedor al entrar en su pestaña (misma
  // regla que la pestaña «Datos proveedor» de la ficha clásica).
  // Eliminar = estado ELIMINADO (no se borra nada): deja de salir en listados,
  // búsquedas y selectores salvo que se filtre por ese estado.
  const eliminar = useMutation({
    mutationFn: () => api.put(`/productos/${id}`, { estado: 'ELIMINADO' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto', id] });
      qc.invalidateQueries({ queryKey: ['productos'] });
      toast.success('Producto eliminado. Solo saldrá si filtras por el estado «Eliminado».');
      volver();
    },
    onError: (e: Error) => toast.error(`No se pudo eliminar: ${e.message}`),
  });
  const pedirEliminar = async () => {
    const ok = await confirmar({
      title: `¿Eliminar ${full.data?.sku ?? 'el producto'}?`,
      message: 'Pasa a estado ELIMINADO y deja de salir en listados, búsquedas y selectores. Sus pedidos, ventas e histórico se conservan. Se recupera cambiando el estado en «Editar».',
      confirmLabel: 'Eliminar',
      danger: true,
    });
    if (ok) eliminar.mutate();
  };
  const vincular = useMutation({
    mutationFn: (proveedor_id: string) => api.post<{ producto_proveedor_id: string | null }>(`/productos/${id}/vincular-proveedor`, { proveedor_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['producto', id, 'full'] }),
  });
  const autolink = useRef(false);
  useEffect(() => {
    const p = full.data;
    if (tab !== 'proveedor' || !p || p.producto_proveedor_id || !p.proveedor_id || autolink.current) return;
    autolink.current = true;
    vincular.mutate(p.proveedor_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, full.data]);

  const ir = (k: SeccionKey) => {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); if (k === 'general') next.delete('tab'); else next.set('tab', k); return next; }, { replace: true });
  };
  const togglePanel = () => { setPanel((v) => { try { localStorage.setItem('pm-approx:ficha-panel', v ? 'off' : 'on'); } catch { /* sin storage */ } return !v; }); };

  const cotizCols = useMemo<Column<Cotizacion>[]>(() => [
    { key: 'fecha', label: 'Fecha', render: (r) => fmt.date(r.fecha), sortValue: (r) => r.fecha },
    { key: 'unidades', label: 'Uds', align: 'right', render: (r) => (r.unidades != null ? fmt.int(r.unidades) : '—'), sortValue: (r) => Number(r.unidades) },
    { key: 'coste_usd', label: 'Coste USD', align: 'right', render: (r) => fmt.usd(r.coste_usd), sortValue: (r) => Number(r.coste_usd) },
    { key: 'cambio_usd_eur', label: 'Cambio', align: 'right', render: (r) => fmt.number(r.cambio_usd_eur), sortValue: (r) => Number(r.cambio_usd_eur) },
    { key: 'cdpe_eur', label: 'CDPe', align: 'right', render: (r) => fmt.eur(r.cdpe_eur), sortValue: (r) => Number(r.cdpe_eur) },
    { key: 'coste_game_final_eur', label: 'Coste final', align: 'right', render: (r) => fmt.eur(r.coste_game_final_eur), sortValue: (r) => Number(r.coste_game_final_eur) },
    { key: 'pvpr_iva_eur', label: 'PVPr c/IVA', align: 'right', render: (r) => fmt.eur(r.pvpr_iva_eur), sortValue: (r) => Number(r.pvpr_iva_eur) },
    { key: 'notas', label: 'Notas', sortValue: (r) => r.notas ?? '', render: (r) => <span className="block max-w-[18rem] truncate" title={r.notas ?? undefined}>{r.notas ?? '—'}</span> },
  ], []);

  if (full.isLoading) return <Spinner />;
  if (full.isError || !full.data) {
    return <div><h1 className="text-xl font-semibold">Producto no encontrado</h1><Button variant="secondary" className="mt-3" onClick={volver}>← Volver al listado</Button></div>;
  }
  const p = full.data;
  const cliente = p.cliente_codigo ?? 'cliente';
  const base = `/${(p.cliente_codigo ?? 'game').toLowerCase()}`;
  const r = resumen.data;
  const cab = r?.cabecera;
  const cont = r?.contadores;
  const productoHref = (otroId: string) => `${base}/productos/${otroId}`;
  const esMensual = cab?.stock.unidad === 'meses';
  const cobBad = cab?.stock.cobertura != null && cab.stock.umbral != null && cab.stock.cobertura < cab.stock.umbral;

  return (
    // Todo el ancho del área de trabajo: con max-w-7xl centrado quedaba media
    // pantalla vacía entre el menú y la ficha en monitores anchos.
    <div className="w-full">
      {/* ── Cabecera ── */}
      <div className="mb-3 flex items-center gap-3 text-xs text-slate-500">
        <button type="button" onClick={volver} className="cursor-pointer hover:text-slate-800">← Productos {cliente}</button>
        <span className="ml-auto flex items-center gap-3">
          <label className="flex cursor-pointer items-center gap-1.5"><input type="checkbox" checked={panel} onChange={togglePanel} /> Panel lateral</label>
          <Link to={`${base}/productos/${p.id}/clasica`} className="underline">Ficha clásica</Link>
        </span>
      </div>

      <header className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1 basis-80">
            <h1 className="text-xl font-bold leading-tight text-slate-900">{p.nombre}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
              <span className="font-mono">{p.sku}</span>
              {p.ean && <span className="font-mono">EAN {p.ean}</span>}
              <Badge color={p.estado_color} dot>{p.estado_nombre ?? p.estado}</Badge>
              <AccionComercialBadge accion={p.accion_comercial} />
              {p.marca_nombre && <Badge tone="neutral">{p.marca_nombre}</Badge>}
              <Badge tone="neutral">{[p.gama_nombre, p.categoria_nombre, p.subcategoria_nombre, p.detalle_nombre].filter(Boolean).join(' › ') || 'Sin clasificar'}</Badge>
              {p.revision_pendiente && <Badge tone="warn" dot>Revisión pendiente</Badge>}
              {p.sustituido_por_id && <Link to={productoHref(p.sustituido_por_id)}><Badge tone="accent" dot>Sustituido por {p.sustituido_por_sku}</Badge></Link>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="edit" size="sm" onClick={() => setEditando(true)}>Editar</Button>
            <Button variant="create" size="sm" onClick={() => { setCalculadora(true); ir('precios'); }}>Cotizar</Button>
            <Button variant="secondary" size="sm" onClick={() => setScrapeOpen(true)} disabled={!p.url_oficial} title={p.url_oficial ? 'Importar ficha desde la URL oficial' : 'Añade la URL oficial en Editar para poder importar'}>Importar de web</Button>
            {p.estado !== 'ELIMINADO' && (
              <Button variant="danger" size="sm" onClick={pedirEliminar} disabled={eliminar.isPending}>{eliminar.isPending ? 'Eliminando…' : 'Eliminar'}</Button>
            )}
          </div>
        </div>

        {p.estado === 'ELIMINADO' && (
          <p role="status" className="mt-3 rounded-md border border-bad-300 bg-bad-50 px-3 py-2 text-xs text-bad-700">
            Producto eliminado: no sale en listados, búsquedas ni selectores salvo que filtres por el estado «Eliminado». Para recuperarlo, cambia el estado en «Editar».
          </p>
        )}

        {/* Los cinco hechos (D2) */}
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <Hecho label="Coste de entrada" valor={cab?.coste_entrada?.valor != null ? fmt.eur(cab.coste_entrada.valor) : cab ? '—' : '…'}
            nota={cab?.coste_entrada ? `recepción ${fmt.date(cab.coste_entrada.fecha)} · ${fmt.int(cab.coste_entrada.uds)} uds` : cab?.coste_teorico != null ? `sin recepciones · coste teórico ${fmt.eur(cab.coste_teorico)}` : 'sin recepciones en los informes'} />
          <Hecho label={`Precio a ${cliente}`} valor={cab?.precio_cliente ? fmt.eur(cab.precio_cliente.valor) : cab ? '—' : '…'}
            nota={cab?.precio_cliente ? `${cab.precio_cliente.fuente === 'ventas_approx' ? 'albarán' : 'pedido'} ${cab.precio_cliente.ref ?? ''} · ${fmt.date(cab.precio_cliente.fecha)}` : 'sin ventas ni pedidos con precio'}
            href={cab?.precio_cliente?.pedido_id ? `${base}/pedidos/${cab.precio_cliente.pedido_id}` : undefined} />
          <Hecho label="Margen APPROX" valor={cab?.margen_pct != null ? <span className={cab.margen_pct < 0 ? 'text-bad-600' : ''}>{fmt.pct(cab.margen_pct)}</span> : cab ? '—' : '…'}
            nota={cab?.margen_pct != null ? 'precio al cliente sobre coste de entrada' : 'faltan precio o coste de entrada'} />
          <Hecho label={`Stock ${cliente}`}
            valor={cab?.stock.actual != null ? <>{fmt.int(cab.stock.actual)}{cab.stock.cobertura != null && <span className={`ml-1.5 text-sm font-semibold ${cobBad ? 'text-warn-600' : 'text-slate-500'}`}>· {fmt.number(cab.stock.cobertura)} {cab.stock.unidad === 'meses' ? 'meses' : 'sem.'}</span>}</> : cab ? '—' : '…'}
            nota={cab?.stock.fecha ? `al ${fmt.date(cab.stock.fecha)}${cab.stock.venta_media != null ? ` · venta ${fmt.number(cab.stock.venta_media)} uds/${esMensual ? 'mes' : 'semana'}${cab.stock.provisional ? ' (mes en curso)' : ''}` : ''}` : 'sin snapshots de stock'} />
          <Hecho label="Próxima entrada" valor={cab?.proxima_entrada ? fmt.int(cab.proxima_entrada.uds) : cab ? '—' : '…'}
            nota={cab?.proxima_entrada ? (cab.proxima_entrada.fuente === 'pedido_cliente' ? `${cab.proxima_entrada.numero ?? 'pedido'} · ${cab.proxima_entrada.eta ? `ETA ${fmt.date(cab.proxima_entrada.eta)}` : 'sin ETA'}${cab.proxima_entrada.total_pendiente > cab.proxima_entrada.uds ? ` · ${fmt.int(cab.proxima_entrada.total_pendiente)} en total` : ''}` : `pendiente de recibir según extracto de ${cliente}`) : 'nada en camino'}
            href={cab?.proxima_entrada?.pedido_id ? `${base}/pedidos/${cab.proxima_entrada.pedido_id}` : undefined} />
        </div>

        {/* Smart buttons */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Smart n={cont?.pedidos} label="pedidos" sub={cont?.pedidos_abiertos ? `${cont.pedidos_abiertos} abiertos` : undefined} onClick={() => ir('pedidos')} />
          <Smart n={cont?.cotizaciones} label="cotizaciones" sub={p.cotizaciones[0] ? `última ${fmt.date(p.cotizaciones[0].fecha)}` : undefined} onClick={() => ir('precios')} />
          <Smart n={cont?.entregas} label="entregas" onClick={() => ir('pedidos')} />
          <Smart n={cont?.ventas_approx} label="ventas APPROX" sub={cont?.abonos_approx ? `${cont.abonos_approx} abonos` : undefined} onClick={() => ir('ventas-approx')} />
          <Smart n={cont?.pedidos_proveedor} label="pedidos a proveedor" onClick={() => ir('proveedor')} />
          <Smart n={cont?.archivos} label="documentos" onClick={() => ir('documentos')} />
        </div>
      </header>

      {/* Pestañas */}
      <div role="tablist" aria-label="Secciones de la ficha" className="mt-3 flex gap-1 overflow-x-auto border-b border-slate-200">
        {SECCIONES.map((s) => {
          const n = contadorPestana(s.key, cont, p.cotizaciones.length);
          return (
            <button key={s.key} type="button" role="tab" aria-selected={tab === s.key} onClick={() => ir(s.key)}
              className={`-mb-px cursor-pointer whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition ${tab === s.key ? 'border-accent-600 text-accent-700' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800'}`}>
              {s.label}{n != null && <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] ${tab === s.key ? 'bg-accent-100 text-accent-700' : 'bg-slate-100 text-slate-500'}`}>{n}</span>}
            </button>
          );
        })}
      </div>

      <div className={`mt-3 grid grid-cols-1 gap-4 ${panel ? 'lg:grid-cols-[minmax(0,1fr)_18rem]' : ''}`}>
        <div className="min-w-0 space-y-4">
          {/* ── General ── */}
          {tab === 'general' && <Seccion titulo="General" resumen="datos maestros, siempre legibles">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div className="md:col-span-8">
                <dl className="grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3">
                  <Campo label="Part number"><span className="font-mono">{p.part_number || <Vacio />}</span></Campo>
                  <Campo label="Proveedor">{p.proveedor_nombre ? <button type="button" onClick={() => ir('proveedor')} className="cursor-pointer text-accent-700 underline">{p.proveedor_nombre}</button> : <Vacio />}{p.ref_proveedor && <span className="ml-1 font-mono text-slate-500">· {p.ref_proveedor}</span>}</Campo>
                  <Campo label="Familia">{p.familia || <Vacio />}</Campo>
                  <Campo label="Lanzamiento">{p.fecha_lanzamiento ? fmt.date(p.fecha_lanzamiento) : <Vacio />}</Campo>
                  <Campo label="Target">{p.precio_target != null ? fmt.eur(p.precio_target) : <Vacio />}</Campo>
                  <Campo label="URL oficial">{p.url_oficial ? <a href={p.url_oficial} target="_blank" rel="noreferrer" className="text-accent-700 underline">Abrir ficha oficial ↗</a> : <Vacio />}</Campo>
                  {p.sustituye_a.length > 0 && <Campo label="Sustituye a">{p.sustituye_a.map((s) => <Link key={s.id} to={productoHref(s.id)} className="mr-2 font-mono text-accent-700 underline">{s.sku}</Link>)}</Campo>}
                  {p.sustituido_por_id && <Campo label="Sustituido por"><Link to={productoHref(p.sustituido_por_id)} className="font-mono text-accent-700 underline">{p.sustituido_por_sku}</Link> <span className="text-slate-500">{p.sustituido_por_nombre}</span></Campo>}
                  {verMas && <>
                    <Campo label="Cliente">{p.cliente_nombre ?? cliente}</Campo>
                    <Campo label="Marca">{p.marca_nombre ?? <Vacio />}</Campo>
                    <Campo label="Gama">{p.gama_nombre ?? <Vacio />}</Campo>
                    <Campo label="Categoría">{p.categoria_nombre ?? <Vacio />}</Campo>
                    <Campo label="Subcategoría">{p.subcategoria_nombre ?? <Vacio />}</Campo>
                    <Campo label="Detalle">{p.detalle_nombre ?? <Vacio />}</Campo>
                    <Campo label="Creado">{fmt.date(p.fecha_creacion)}</Campo>
                    <Campo label="Coste teórico · PVPr">{p.coste != null ? fmt.eur(p.coste) : '—'} · {p.pvpr != null ? fmt.eur(p.pvpr) : '—'}</Campo>
                  </>}
                </dl>
                <button type="button" onClick={() => setVerMas((v) => !v)} className="mt-3 cursor-pointer text-xs text-accent-700 underline">{verMas ? 'Mostrar menos' : 'Mostrar clasificación, cliente y fechas'}</button>
                <div className="mt-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Notas internas</div>
                  {p.notas ? <p className="mt-0.5 whitespace-pre-line text-sm text-slate-700">{p.notas}</p> : <p className="mt-0.5 text-sm italic text-slate-400">Sin notas.</p>}
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between"><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Ficha comercial</div>{p.url_oficial && <button type="button" onClick={() => setScrapeOpen(true)} className="cursor-pointer text-xs text-accent-700 underline">Importar desde la URL oficial</button>}</div>
                  {p.ficha_descripcion
                    ? (/<\/?[a-z][^>]*>/i.test(p.ficha_descripcion)
                      ? <div className="prose prose-sm mt-0.5 max-w-none text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.ficha_descripcion) }} />
                      : <p className="mt-0.5 whitespace-pre-line text-sm text-slate-700">{p.ficha_descripcion}</p>)
                    : heredado.data?.descripcion
                      ? <DescripcionProveedor texto={heredado.data.descripcion} origen={heredado.data.producto_proveedor?.ref_proveedor} className="mt-0.5" />
                      : <p className="mt-0.5 text-sm italic text-slate-400">Sin ficha comercial. Pulsa «Editar» o importa desde la URL oficial.</p>}
                </div>
              </div>
              <div className="md:col-span-4"><ProductoFotos productoId={p.id} /><RotacionSubtipo productoId={p.id} /></div>
            </div>
          </Seccion>}

          {/* ── Precios y costes ── */}
          {tab === 'precios' && <Seccion titulo="Precios y costes" resumen="las cifras, alineadas y con su fecha"
            acciones={<Button size="sm" variant={calculadora ? 'secondary' : 'create'} onClick={() => setCalculadora((v) => !v)}>{calculadora ? 'Cerrar calculadora' : 'Nueva cotización'}</Button>}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              <Cifra label="Coste fábrica (USD)" valor={p.cotizaciones[0]?.coste_usd != null ? fmt.usd(p.cotizaciones[0].coste_usd) : '—'} nota={p.cotizaciones[0] ? `cotización ${fmt.date(p.cotizaciones[0].fecha)} · cambio ${fmt.number(p.cotizaciones[0].cambio_usd_eur)}` : 'sin cotizaciones'} />
              <Cifra label="CDPe cotizado" valor={p.cotizaciones[0]?.cdpe_eur != null ? fmt.eur(p.cotizaciones[0].cdpe_eur) : '—'} nota="con transporte y aranceles" />
              <Cifra label="Coste de entrada real" valor={cab?.coste_entrada?.valor != null ? fmt.eur(cab.coste_entrada.valor) : '—'} nota={cab?.coste_entrada ? `recepción ${fmt.date(cab.coste_entrada.fecha)}` : 'sin recepciones'} />
              <Cifra label={`Precio a ${cliente}`} valor={cab?.precio_cliente ? fmt.eur(cab.precio_cliente.valor) : '—'} nota={cab?.precio_cliente ? `${fmt.date(cab.precio_cliente.fecha)}` : '—'} />
              <Cifra label={`PVPr ${cliente}`} valor={p.pvpr != null ? fmt.eur(p.pvpr) : '—'} nota={p.margen_pct != null ? `margen del cliente ${fmt.pct(p.margen_pct)} · IVA incl.` : 'con IVA'} />
            </div>
            {calculadora && (
              <div className="mt-4 rounded-md border border-accent-300 bg-accent-50/40 p-3">
                <GameCalculadora clienteCodigo={cliente} embebida={{ producto: { id: p.id, gama_id: p.gama_id, categoria_id: p.categoria_id, subcategoria_id: p.subcategoria_id, proveedor_id: p.proveedor_id, ref_proveedor: p.ref_proveedor }, onSaved: () => { qc.invalidateQueries({ queryKey: ['producto', p.id] }); setCalculadora(false); } }} />
              </div>
            )}
            <div className="mt-4">
              {p.cotizaciones.length === 0
                ? <p className="text-sm italic text-slate-400">Todavía no hay cotizaciones de este producto. «Nueva cotización» abre la calculadora aquí mismo.</p>
                : <DataTable<Cotizacion> columns={cotizCols} rows={p.cotizaciones} rowKey={(c) => c.id} searchable={false} stickyHeader={false} syncUrl={false} defaultSort={{ key: 'fecha', desc: true }}
                    onRowClick={(c) => navigate(`${base}/cotizaciones/${c.id}`)} />}
            </div>
          </Seccion>}

          {/* ── Stock y ventas ── */}
          {tab === 'stock' && <Seccion titulo="Stock y ventas" resumen={cab?.stock.fecha ? `extracto de ${cliente} del ${fmt.date(cab.stock.fecha)}` : 'sin extractos de stock'}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Cifra label="Disponible" valor={cab?.stock.actual != null ? fmt.int(cab.stock.actual) : '—'} nota={cab?.stock.fecha ? fmt.date(cab.stock.fecha) : ''} />
              <Cifra label="Pendiente recibir" valor={cab?.proxima_entrada ? fmt.int(cab.proxima_entrada.total_pendiente) : '—'} nota={cab?.proxima_entrada?.eta ? `ETA ${fmt.date(cab.proxima_entrada.eta)}` : cab?.proxima_entrada ? 'sin ETA' : 'nada en camino'} />
              <Cifra label="Venta media" valor={cab?.stock.venta_media != null ? <>{fmt.number(cab.stock.venta_media)} <span className="text-xs font-normal">uds/{esMensual ? 'mes' : 'sem.'}</span></> : '—'} nota={esMensual ? 'media de los 3 últimos meses con venta' : 'media de las 4 últimas semanas'} />
              <Cifra label={esMensual ? 'Meses de stock' : 'Semanas de stock'} valor={cab?.stock.cobertura != null ? <span className={cobBad ? 'text-warn-600' : ''}>{fmt.number(cab.stock.cobertura)}</span> : '—'} nota={cab?.stock.umbral != null ? `aviso por debajo de ${cab.stock.umbral}` : ''} />
            </div>
            {r && (r.ventas_mensuales.length > 1 || r.ventas_semanales.length > 1) && (
              <Tendencia mensual={r.ventas_mensuales} semanal={r.ventas_semanales} />
            )}
            {r && r.ventas_mensuales.length > 0 && (
              <div className="mt-3">
                <DataTable<{ mes: string; unidades: number; provisional: boolean }> columns={[
                  { key: 'mes', label: 'Mes', render: (x) => `${x.mes.slice(5, 7)}/${x.mes.slice(0, 4)}${x.provisional ? ' · en curso' : ''}`, sortValue: (x) => x.mes },
                  { key: 'unidades', label: 'Unidades vendidas', align: 'right', render: (x) => fmt.int(x.unidades), sortValue: (x) => x.unidades, total: 'sum', totalFormat: (n) => fmt.int(n) },
                ]} rows={r.ventas_mensuales} rowKey={(x) => x.mes} searchable={false} stickyHeader={false} syncUrl={false} defaultSort={{ key: 'mes', desc: true }} />
              </div>
            )}
            {r && r.ventas_mensuales.length === 0 && p.historico_semanal.length > 0 && (
              <div className="mt-3">
                <DataTable<SemanaRow> columns={[
                  { key: 'semana_inicio', label: 'Semana', render: (x) => `${fmt.date(x.semana_inicio)} → ${fmt.date(x.semana_fin)}`, sortValue: (x) => x.semana_inicio },
                  { key: 'unidades_vendidas', label: 'Vendidas', align: 'right', render: (x) => fmt.int(x.unidades_vendidas), sortValue: (x) => Number(x.unidades_vendidas), total: 'sum', totalFormat: (n) => fmt.int(n) },
                  { key: 'stock_actual', label: 'Stock', align: 'right', render: (x) => (x.stock_actual != null ? fmt.int(x.stock_actual) : '—'), sortValue: (x) => Number(x.stock_actual) },
                ]} rows={p.historico_semanal} rowKey={(x) => x.fecha_informe + x.semana_inicio} searchable={false} syncUrl={false} maxBodyHeight="20rem" defaultSort={{ key: 'semana_inicio', desc: true }} />
              </div>
            )}
            {r && r.ventas_mensuales.length === 0 && p.historico_semanal.length === 0 && <p className="mt-3 text-sm italic text-slate-400">Sin ventas importadas para este producto.</p>}
          </Seccion>}

          {/* ── Coste de entrada ── */}
          {tab === 'coste-entrada' && <CosteEntradaFicha productoId={p.id} clienteCodigo={cliente} clienteId={p.cliente_id ?? null} />}

          {/* ── Ventas APPROX ── */}
          {tab === 'ventas-approx' && <VentasApproxFicha productoId={p.id} clienteCodigo={cliente} forzar />}

          {/* ── Pedidos y entregas ── */}
          {tab === 'pedidos' && (
            <div className="space-y-4">
              <HistoricoPrecioVentaSection productoId={p.id} clienteCodigo={cliente} />
              <EntregasProductoSection productoId={p.id} productoNombre={p.nombre} clienteCodigo={cliente} />
            </div>
          )}

          {/* ── Proveedor ── */}
          {tab === 'proveedor' && <Seccion titulo="Proveedor" resumen={p.proveedor_nombre ? `${p.proveedor_nombre}${p.ref_proveedor ? ` · ref ${p.ref_proveedor}` : ''}` : 'sin proveedor asignado'}
            acciones={p.producto_proveedor_id ? <Button size="sm" variant="secondary" onClick={() => navigate(`/proveedores/productos/${p.producto_proveedor_id}`)}>Ir a la ficha de proveedor →</Button> : undefined}>
            {!p.proveedor_id && <p className="text-sm text-slate-500">Asigna un proveedor en «Editar» para enlazar su ficha (precios de compra, MOQ, plazos, logística).</p>}
            {p.proveedor_id && (p.producto_proveedor_id ? <ProductoProveedorDetalleView id={p.producto_proveedor_id} embedded /> : <p className="text-sm text-slate-500">{vincular.isPending ? 'Enlazando la ficha de proveedor…' : 'No hay ficha de proveedor enlazada todavía.'}</p>)}
          </Seccion>}

          {/* ── Documentos ── */}
          {tab === 'documentos' && <ProductoArchivos productoId={p.id} categoriaNombre={p.categoria_nombre} />}
        </div>

        {/* ── Panel lateral ── */}
        {panel && (
          <aside className="space-y-3 lg:sticky lg:top-12 lg:self-start">
            <Panel titulo="Alertas">
              {r ? (r.alertas.length ? r.alertas.map((a, i) => (
                <div key={i} className="flex items-start gap-2 py-1 text-xs"><Badge tone={a.tono} dot>{a.titulo}</Badge><span className="text-slate-700">{a.detalle}</span></div>
              )) : <p className="text-xs text-ok-700">Nada que avisar.</p>) : <p className="text-xs text-slate-400">Cargando…</p>}
            </Panel>
            <Panel titulo="Proveedor">
              {p.proveedor_nombre ? <>
                <div className="text-sm font-semibold">{p.proveedor_nombre}</div>
                <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  {p.ref_proveedor && <><dt className="text-slate-500">Ref.</dt><dd className="text-right font-mono">{p.ref_proveedor}</dd></>}
                  {cab?.coste_entrada && <><dt className="text-slate-500">Último coste entrada</dt><dd className="text-right font-semibold">{fmt.eur(cab.coste_entrada.valor)}</dd></>}
                  {p.cotizaciones[0]?.coste_usd != null && <><dt className="text-slate-500">Último coste fábrica</dt><dd className="text-right">{fmt.usd(p.cotizaciones[0].coste_usd)}</dd></>}
                </dl>
              </> : <p className="text-xs text-slate-500">Sin proveedor asignado.</p>}
            </Panel>
            <Panel titulo="Próximo hito">
              {cab?.proxima_entrada ? <>
                <div className="text-sm font-semibold">{cab.proxima_entrada.eta ? `Recepción ${fmt.date(cab.proxima_entrada.eta)}` : 'Entrada sin fecha'}</div>
                <div className="text-xs text-slate-600">{fmt.int(cab.proxima_entrada.uds)} uds{cab.proxima_entrada.numero ? ` · ${cab.proxima_entrada.numero}` : ''}{cab.proxima_entrada.estado ? ` · ${cab.proxima_entrada.estado.toLowerCase().replace(/_/g, ' ')}` : ''}</div>
              </> : <p className="text-xs text-slate-500">Sin entradas previstas.</p>}
            </Panel>
            {r?.ventas_approx_hasta && <Panel titulo="Datos"><p className="text-xs text-slate-600">Ventas de APPROX hasta el {fmt.date(r.ventas_approx_hasta)}.</p></Panel>}
            <Panel titulo="Atajos"><p className="text-xs text-slate-500"><Link to={`${base}/importaciones`} className="underline">Ficheros importados</Link> · <Link to={`${base}/cotizaciones`} className="underline">Cotizaciones</Link> · <Link to={`${base}/pedidos`} className="underline">Pedidos</Link></p></Panel>
          </aside>
        )}
      </div>

      <ProductoEditarModal producto={p} open={editando} onClose={() => setEditando(false)} onSaved={() => setEditando(false)} />
      <ProductoScrapeModal productoId={p.id} urlOficial={p.url_oficial} open={scrapeOpen} onClose={() => setScrapeOpen(false)} />
    </div>
  );
}

// ── Piezas ──────────────────────────────────────────────────────────────

function Hecho({ label, valor, nota, href }: { label: string; valor: ReactNode; nota?: string; href?: string }) {
  const cuerpo = (
    <>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 font-display text-lg font-extrabold leading-tight text-slate-900">{valor}</div>
      {nota && <div className="mt-0.5 truncate text-[11px] text-slate-500" title={nota}>{nota}</div>}
    </>
  );
  const cls = 'block min-w-0 rounded-md border border-slate-200 bg-slate-50 px-3 py-2';
  return href ? <Link to={href} className={`${cls} hover:border-accent-300 hover:bg-accent-50`}>{cuerpo}</Link> : <div className={cls}>{cuerpo}</div>;
}

function Smart({ n, label, sub, onClick }: { n: number | undefined; label: string; sub?: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-600 hover:border-accent-300 hover:bg-accent-50">
      <span className="font-display text-sm font-extrabold text-slate-900">{n ?? '…'}</span>{label}{sub && <span className="text-slate-400">· {sub}</span>}
    </button>
  );
}

/** Contador que se pinta en la pestaña (null = sin contador). */
function contadorPestana(k: SeccionKey, cont: Resumen['contadores'] | undefined, nCotizaciones: number): number | null {
  if (!cont) return null;
  switch (k) {
    case 'precios': return nCotizaciones;
    case 'coste-entrada': return null;
    case 'ventas-approx': return cont.ventas_approx + cont.abonos_approx;
    case 'pedidos': return cont.pedidos;
    case 'documentos': return cont.archivos;
    default: return null;
  }
}

function Seccion({ titulo, resumen, acciones, children }: { titulo: string; resumen?: string; acciones?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-5 py-3">
        <h2 className="text-sm font-bold text-slate-900">{titulo}</h2>
        {resumen && <span className="text-xs text-slate-500">· {resumen}</span>}
        {acciones && <div className="ml-auto flex gap-2">{acciones}</div>}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Panel({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-3 shadow-sm">
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">{titulo}</div>
      {children}
    </div>
  );
}

function Campo({ label, children }: { label: string; children: ReactNode }) {
  return <div className="min-w-0"><dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt><dd className="mt-0.5 break-words text-sm text-slate-800">{children}</dd></div>;
}
function Cifra({ label, valor, nota }: { label: string; valor: ReactNode; nota?: string }) {
  return <div className="rounded-md border border-slate-200 px-3 py-2"><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div><div className="font-display text-base font-extrabold text-slate-900">{valor}</div>{nota && <div className="truncate text-[11px] text-slate-500" title={nota}>{nota}</div>}</div>;
}
function Vacio() { return <span className="italic text-slate-400">—</span>; }

/** Miniatura de tendencia: ventas (línea) y stock (discontinua) de las últimas 12 semanas o meses. */
function Tendencia({ mensual, semanal }: { mensual: Resumen['ventas_mensuales']; semanal: Resumen['ventas_semanales'] }) {
  const puntos = mensual.length > 1 ? mensual.map((m) => ({ x: m.mes, v: m.unidades, s: null as number | null })) : semanal.map((s) => ({ x: s.semana_inicio, v: s.unidades, s: s.stock }));
  const W = 600; const H = 60; const pad = 4;
  const vs = puntos.map((p) => p.v); const ss = puntos.map((p) => p.s ?? 0);
  const max = Math.max(1, ...vs, ...ss);
  const min = Math.min(0, ...vs);
  const y = (n: number) => H - pad - ((n - min) / (max - min || 1)) * (H - pad * 2);
  const x = (i: number) => pad + (i / Math.max(1, puntos.length - 1)) * (W - pad * 2);
  const linea = puntos.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const stock = puntos.some((p) => p.s != null) ? puntos.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.s ?? 0).toFixed(1)}`).join(' ') : null;
  const primero = puntos[0]; const ultimo = puntos[puntos.length - 1];
  const etiqueta = (v: string) => (v.length >= 7 ? `${v.slice(5, 7)}/${v.slice(0, 4)}` : v);
  return (
    <div className="mt-3">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-14 w-full" role="img" aria-label="Tendencia de ventas">
        <path d={`${linea} L${x(puntos.length - 1).toFixed(1)},${H} L${pad},${H} Z`} fill="#2456B8" fillOpacity="0.08" />
        <path d={linea} fill="none" stroke="#2456B8" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        {stock && <path d={stock} fill="none" stroke="#A15A05" strokeWidth="1.5" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />}
        <circle cx={x(puntos.length - 1)} cy={y(ultimo.v)} r="3" fill="#2456B8" />
      </svg>
      <div className="flex justify-between text-[11px] text-slate-500"><span>{etiqueta(primero.x)}</span><span><span className="inline-block h-0 w-3 border-t-2 border-accent-600 align-middle" /> ventas{stock && <> · <span className="inline-block h-0 w-3 border-t-2 border-dashed border-warn-600 align-middle" /> stock</>}</span><span>{etiqueta(ultimo.x)}: <b className="text-slate-800">{fmt.int(ultimo.v)}</b></span></div>
    </div>
  );
}
